/**
 * Fiche d'un PROGRAMME officiel (admin, migration 0292) : titre, texte
 * officiel, date de rentrée (obligatoire), date de fin (facultative), et le
 * texte « Couverture du programme » du site (migration 0507). C'est le
 * programme qui porte la validité ; ses packs portent un planning indicatif.
 */

import { useState } from 'react';
import { Button } from '@jeraisonne/ui';
import { programmePeriodIssue } from '@jeraisonne/db/curriculum';
import { useSaveProgramme, type Programme } from '@/lib/curriculum/api';

const inputClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none';

export function ProgrammeForm({
  level,
  subject,
  programme,
  defaultTitle,
  onDone,
}: {
  level: string;
  subject: string;
  programme: Programme | null;
  defaultTitle: string;
  onDone: (id?: string) => void;
}) {
  const save = useSaveProgramme(level, subject);
  const [title, setTitle] = useState(programme?.title ?? defaultTitle);
  const [source, setSource] = useState(programme?.source ?? '');
  const [from, setFrom] = useState(programme?.valid_from ?? '');
  const [until, setUntil] = useState(programme?.valid_until ?? '');
  const [coverage, setCoverage] = useState(programme?.coverage_md ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setError(null);
    const issue =
      title.trim().length < 2
        ? 'Donne un titre au programme.'
        : programmePeriodIssue(from, until || null);
    if (issue) {
      setError(issue);
      return;
    }
    try {
      const id = await save.mutateAsync({
        id: programme?.id,
        input: {
          level,
          subject,
          title: title.trim(),
          source: source.trim() || null,
          valid_from: from,
          valid_until: until || null,
          coverage_md: coverage.trim() || null,
        },
      });
      onDone(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div
      className="border-primary-200 space-y-3 rounded-2xl border bg-white p-4"
      data-testid="programme-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-ink-700 text-sm font-semibold">Titre du programme</span>
          <input
            className={inputClass}
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-ink-700 text-sm font-semibold">Texte officiel</span>
          <input
            className={inputClass}
            value={source}
            placeholder="BO n° …, arrêté du …"
            onChange={(e) => setSource(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">
            Date de rentrée (entrée en vigueur)
          </span>
          <input
            type="date"
            required
            className={inputClass}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">
            Date de fin <span className="text-ink-400 font-normal">(facultative)</span>
          </span>
          <input
            type="date"
            className={inputClass}
            value={until}
            min={from || undefined}
            onChange={(e) => setUntil(e.target.value)}
          />
        </label>
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-ink-700 text-sm font-semibold">
            Couverture du programme{' '}
            <span className="text-ink-400 font-normal">
              (affichée sur la page programme du site)
            </span>
          </span>
          <textarea
            className={`${inputClass} min-h-[8rem]`}
            value={coverage}
            maxLength={2000}
            placeholder={
              'Ce que les SKILLCARDS couvrent…\n\nÀ venir : les cartes qui manquent encore.'
            }
            onChange={(e) => setCoverage(e.target.value)}
          />
          <span className="text-ink-400 block text-xs">
            Un paragraphe par ligne vide. Commence un paragraphe par « À venir : » pour le mettre en
            valeur.
            {programme?.coverage_updated_at &&
              ` Mis à jour le ${new Date(programme.coverage_updated_at).toLocaleDateString('fr-FR')}.`}
          </span>
        </label>
      </div>
      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" onClick={() => void onSave()} disabled={save.isPending}>
          Enregistrer le programme
        </Button>
        <Button type="button" variant="secondary" onClick={() => onDone()}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
